import { z } from 'zod'
import {
  ApprovalGroupSchema,
  ApproverSchema,
  BulkCreateItemApprovalsRequestSchema,
  EditApprovalMessageRequestSchema,
  ItemApprovalSchema,
  ManageApproversRequestSchema,
  RedactedApproverSchema,
  RedactedItemApprovalSchema,
  RejectApproverRequestSchema,
  SendApprovalRequestsRequestSchema,
} from '../schemas'
import { http } from './http'
import { auth } from './auth'
import { parseWithZod } from '../utils/parse-with-zod'
import type {
  BulkCreateItemApprovalsRequest,
  EditApprovalMessageRequest,
  ManageApproversRequest,
  RejectApproverRequest,
  SendApprovalRequestsRequest,
} from '../types'

const ItemApprovalResponseSchema = z.object({ item_approval: ItemApprovalSchema })
const ItemApprovalsListResponseSchema = z.object({
  item_approvals: z.array(ItemApprovalSchema),
})
const RedactedItemApprovalsListResponseSchema = z.object({
  item_approvals: z.array(RedactedItemApprovalSchema),
})
const ApprovalGroupResponseSchema = z.object({ approval_group: ApprovalGroupSchema })
const ApprovalGroupsListResponseSchema = z.object({
  approval_groups: z.array(ApprovalGroupSchema),
})
const ApproverResponseSchema = z.object({ approver: ApproverSchema })
const ApproversListResponseSchema = z.object({ approvers: z.array(ApproverSchema) })
const RedactedApproversListResponseSchema = z.object({
  approvers: z.array(RedactedApproverSchema),
})
const BulkCreateItemApprovalsResponseSchema = z.object({
  job_id: z.string().nullable(),
  item_approval: ItemApprovalSchema,
  approval_groups: z.array(ApprovalGroupSchema),
  approvers: z.array(ApproverSchema),
})
const ManageApproversResponseSchema = z.object({
  approval_groups: z.array(ApprovalGroupSchema),
  approvers: z.array(ApproverSchema),
})

export const approvals = {
  // ---- Item approvals -----------------------------------------

  listForDeal: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(
      `${regionHost}/api/deals/${dealId}/item_approvals`,
      undefined,
      dealId,
    )
    return parseWithZod(ItemApprovalsListResponseSchema, result).item_approvals
  },

  listRedactedForDeal: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(
      `${regionHost}/api/deals/${dealId}/redacted_item_approvals`,
      undefined,
      dealId,
    )
    return parseWithZod(RedactedItemApprovalsListResponseSchema, result).item_approvals
  },

  getById: async (id: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/item_approvals/${id}`, undefined, dealId)
    return parseWithZod(ItemApprovalResponseSchema, result).item_approval
  },

  // ---- Approval groups ----------------------------------------

  listGroupsForDeal: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(
      `${regionHost}/api/deals/${dealId}/approval_groups`,
      undefined,
      dealId,
    )
    return parseWithZod(ApprovalGroupsListResponseSchema, result).approval_groups
  },

  listGroupsForApproval: async (approvalId: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(
      `${regionHost}/api/item_approvals/${approvalId}/approval_groups`,
      undefined,
      dealId,
    )
    return parseWithZod(ApprovalGroupsListResponseSchema, result).approval_groups
  },

  getGroupById: async (id: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/approval_groups/${id}`, undefined, dealId)
    return parseWithZod(ApprovalGroupResponseSchema, result).approval_group
  },

  // ---- Approvers ----------------------------------------------

  listApproversForDeal: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/deals/${dealId}/approvers`, undefined, dealId)
    return parseWithZod(ApproversListResponseSchema, result).approvers
  },

  listApproversForApproval: async (approvalId: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(
      `${regionHost}/api/item_approvals/${approvalId}/approvers`,
      undefined,
      dealId,
    )
    return parseWithZod(ApproversListResponseSchema, result).approvers
  },

  listRedactedApproversForDeal: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(
      `${regionHost}/api/deals/${dealId}/redacted_approvers`,
      undefined,
      dealId,
    )
    return parseWithZod(RedactedApproversListResponseSchema, result).approvers
  },

  getApproverById: async (id: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/approvers/${id}`, undefined, dealId)
    return parseWithZod(ApproverResponseSchema, result).approver
  },

  // ---- Mutations ----------------------------------------------

  bulkCreate: async (dealId: number, payload: BulkCreateItemApprovalsRequest) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/deals/${dealId}/bulk_create_item_approvals`,
      parseWithZod(BulkCreateItemApprovalsRequestSchema, payload),
      dealId,
    )
    return parseWithZod(BulkCreateItemApprovalsResponseSchema, result)
  },

  manageApprovers: async (approvalId: number, payload: ManageApproversRequest, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/item_approvals/${approvalId}/manage_approvers`,
      parseWithZod(ManageApproversRequestSchema, payload),
      dealId,
    )
    return parseWithZod(ManageApproversResponseSchema, result)
  },

  sendRequests: async (dealId: number, payload: SendApprovalRequestsRequest) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/deals/${dealId}/send_approval_requests`,
      parseWithZod(SendApprovalRequestsRequestSchema, payload),
      dealId,
    )
    return parseWithZod(ApproversListResponseSchema, result).approvers
  },

  editMessage: async (approvalId: number, payload: EditApprovalMessageRequest, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/item_approvals/${approvalId}/edit_approval_message`,
      parseWithZod(EditApprovalMessageRequestSchema, payload),
      dealId,
    )
    return parseWithZod(ItemApprovalResponseSchema, result).item_approval
  },

  delete: async (approvalId: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    await http.delete(`${regionHost}/api/item_approvals/${approvalId}`, dealId)
  },

  approve: async (approverId: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(`${regionHost}/api/approvers/${approverId}/approve`, {}, dealId)
    return parseWithZod(ApproverResponseSchema, result).approver
  },

  reject: async (approverId: number, payload: RejectApproverRequest, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/approvers/${approverId}/reject`,
      parseWithZod(RejectApproverRequestSchema, payload),
      dealId,
    )
    return parseWithZod(ApproverResponseSchema, result).approver
  },
}
